import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { CoreService } from 'src/app/services/core.service';
import { AuthGoogleSigninButtonComponent } from '@features/auth/presentation/components/auth-google-signin-button/auth-google-signin-button.component';
import { AuthShowcaseAsideComponent } from '@features/auth/presentation/components/auth-showcase-aside/auth-showcase-aside.component';
import { AuthScrollOnFocusDirective } from '@features/auth/presentation/utils/auth-scroll-on-focus.directive';
import { AuthService } from '@features/auth/services/auth.service';
import { clearOnboardDraft } from '@features/onboard/presentation/utils/onboard-draft.storage';
import { ToastService } from '@shared/ui/toast/toast.service';
import {
  authErrorI18nKey,
  resolveAuthRequestErrorKind,
} from '@features/auth/utils/auth-request-error.util';
import { prepareGoogleSignInUi } from '@features/auth/utils/google-auth-ready.util';
import {
  canonicalizeWebsitePlanCode,
  patchWebsiteClaim,
  readWebsiteClaim,
  suggestWebsiteSlugFromName,
  type WebsitePlanCode,
} from '@features/website/public-api';
import { navigateAfterStaffAuth } from '@features/auth/utils/navigate-after-staff-auth.util';
import { setCreateBusinessIntent } from '@core/auth/create-business-intent.storage';
import { SessionUser } from '@core/auth/auth-session.model';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import { LegalPublicService } from '@features/legal/public-api';
import { LocationContextService } from '@core/tenant/location-context.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';

const EMAIL_FORMAT_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

@Component({
  selector: 'app-side-register',
  imports: [
    RouterModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    AuthGoogleSigninButtonComponent,
    AuthShowcaseAsideComponent,
    AuthScrollOnFocusDirective,
    TranslateModule,
    TablerIconsModule,
  ],
  templateUrl: './side-register.component.html',
})
export class AppSideRegisterComponent implements OnInit, OnDestroy {
  private readonly settings = inject(CoreService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly socialAuthService = inject(SocialAuthService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly adminShellMode = inject(AdminShellModeService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly locationContext = inject(LocationContextService);
  private readonly legalPublic = inject(LegalPublicService);

  options = this.settings.getOptions();
  private authSub?: Subscription;

  showGoogleSignIn = false;
  googleAuthInitializing = true;
  hidePassword = true;
  isSubmitting = false;
  errorMessage = '';
  emailAlreadyRegistered = false;
  validationAttempted = false;
  claimBusinessName = '';
  claimSlug = '';
  readonly passwordRules = ['length', 'uppercase', 'lowercase', 'number'] as const;

  get loginWithCreateBusinessQuery(): { email?: string; intent: string } {
    const email = (this.form.value.email ?? '').trim();
    return email
      ? { email, intent: 'create_business' }
      : { intent: 'create_business' };
  }

  ngOnInit(): void {
    // Registro = alta de negocio propio. Evita aterrizar en un tenant viejo tras Google OAuth.
    setCreateBusinessIntent();

    const emailParam = this.route.snapshot.queryParamMap.get('email');
    if (emailParam?.trim()) {
      this.form.patchValue({ email: emailParam.trim() });
    }

    const nameParam = this.route.snapshot.queryParamMap.get('name')?.trim() ?? '';
    const slugParam = this.route.snapshot.queryParamMap.get('slug')?.trim() ?? '';
    const planParam = this.route.snapshot.queryParamMap.get('plan');
    const existingClaim = readWebsiteClaim();
    const planFromQuery = canonicalizeWebsitePlanCode(planParam);
    const planFromClaim = canonicalizeWebsitePlanCode(existingClaim?.planCode);
    // No pisar un claim Vender/Emprende/Opera con default link si falta ?plan=.
    const planCode: WebsitePlanCode = planFromQuery || planFromClaim || 'link';
    if (nameParam && slugParam) {
      patchWebsiteClaim({ name: nameParam, slug: slugParam, planCode });
    } else {
      patchWebsiteClaim({ planCode });
    }
    const claim = readWebsiteClaim();
    this.claimBusinessName = nameParam || claim?.name?.trim() || '';
    this.claimSlug = slugParam || claim?.slug?.trim() || '';
    if (this.claimBusinessName) {
      this.form.patchValue({ businessName: this.claimBusinessName });
    }

    void this.initGoogleAuth();
  }

  private async initGoogleAuth(): Promise<void> {
    await prepareGoogleSignInUi(this.socialAuthService);

    this.googleAuthInitializing = false;
    this.showGoogleSignIn = true;
    this.authSub = this.socialAuthService.authState
      .pipe(distinctUntilChanged((prev, next) => prev?.idToken === next?.idToken))
      .subscribe((user) => {
        if (!user?.idToken || this.isSubmitting) {
          return;
        }
        this.isSubmitting = true;
        this.errorMessage = '';
        // Reafirmar intent por si el usuario limpió storage entre tanto.
        setCreateBusinessIntent();
        const fromGoogle = [user.firstName, user.lastName]
          .map((part) => (part ?? '').trim())
          .filter(Boolean)
          .join(' ') || (user.name ?? '').trim();
        const businessName = this.resolveBrandName(fromGoogle) || undefined;
        this.persistBusinessClaimFromForm(fromGoogle);
        this.authService.loginWithGoogle(user.idToken, { businessName }).subscribe({
          next: (result) => {
            clearOnboardDraft();
            this.toast.successTranslated('auth.register.successToast');
            this.legalPublic.acceptServiceDocs('register_owner').subscribe({
              next: () =>
                void this.navigateAfterAuth(result.user).finally(() => {
                  this.isSubmitting = false;
                }),
              error: () =>
                void this.navigateAfterAuth(result.user).finally(() => {
                  this.isSubmitting = false;
                }),
            });
          },
          error: (err: unknown) => {
            this.isSubmitting = false;
            const kind = resolveAuthRequestErrorKind(err);
            this.errorMessage = this.translate.instant(authErrorI18nKey('auth.register', kind));
          },
        });
      });
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
  }

  form = new FormGroup({
    uname: new FormControl('', [Validators.required, Validators.minLength(2)]),
    lname: new FormControl('', [Validators.required, Validators.minLength(2)]),
    /** Opcional: si vacío, wizard/lead usan nombre + apellido. */
    businessName: new FormControl('', [Validators.minLength(2)]),
    email: new FormControl('', [Validators.required, Validators.pattern(EMAIL_FORMAT_PATTERN)]),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.pattern(/[A-Z]/),
      Validators.pattern(/[a-z]/),
      Validators.pattern(/[0-9]/),
    ]),
    legalAccepted: new FormControl(false, [Validators.requiredTrue]),
  });

  get f() {
    return this.form.controls;
  }

  passwordMeets(rule: 'length' | 'uppercase' | 'lowercase' | 'number'): boolean {
    const password = this.form.controls.password.value ?? '';
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
    }[rule];
  }

  /** Contraseña cumple las 4 reglas (lista oculta + check en el campo). */
  passwordIsValid(): boolean {
    return this.passwordRules.every((rule) => this.passwordMeets(rule));
  }

  /** Mostrar checklist solo mientras valida / aún no cumple. */
  showPasswordRules(): boolean {
    if (this.passwordIsValid()) {
      return false;
    }
    const ctrl = this.form.controls.password;
    const value = ctrl.value ?? '';
    return value.length > 0 || ctrl.dirty || ctrl.touched || this.validationAttempted;
  }

  showFieldError(control: FormControl): boolean {
    return (this.validationAttempted || control.dirty) && control.invalid;
  }

  submit(): void {
    this.validationAttempted = true;
    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }

    this.errorMessage = '';
    this.emailAlreadyRegistered = false;
    this.isSubmitting = true;

    const businessName = this.resolveBrandName();
    this.persistBusinessClaimFromForm();

    this.authService
      .register({
        name: this.form.value.uname ?? '',
        lastName: this.form.value.lname ?? '',
        businessName: businessName || undefined,
        email: this.form.value.email ?? '',
        password: this.form.value.password ?? '',
      })
      .subscribe({
        next: (result) => {
          clearOnboardDraft();
          setCreateBusinessIntent();
          this.toast.successTranslated('auth.register.successToast');
          this.legalPublic.acceptServiceDocs('register_owner').subscribe({
            next: () =>
              void this.navigateAfterAuth(result.user).finally(() => {
                this.isSubmitting = false;
              }),
            error: () =>
              void this.navigateAfterAuth(result.user).finally(() => {
                this.isSubmitting = false;
              }),
          });
        },
        error: (err: unknown) => {
          this.isSubmitting = false;
          const kind = resolveAuthRequestErrorKind(err);
          this.emailAlreadyRegistered = kind === 'email_already';
          this.errorMessage = this.translate.instant(authErrorI18nKey('auth.register', kind));
          if (this.emailAlreadyRegistered) {
            setCreateBusinessIntent();
          }
        },
      });
  }

  /**
   * Marca comercial para claim/wizard/lead:
   * negocio si lo escribió; si no, nombre + apellido (o fallback, p. ej. Google).
   */
  private resolveBrandName(personFallback = ''): string {
    const business = (this.form.value.businessName ?? '').trim();
    if (business) {
      return business;
    }
    const person = [this.form.value.uname, this.form.value.lname]
      .map((part) => (part ?? '').trim())
      .filter(Boolean)
      .join(' ');
    return person || personFallback.trim();
  }

  /** Guarda nombre + slug para que el wizard de onboarding los precargue. */
  private persistBusinessClaimFromForm(personFallback = ''): void {
    const brandName = this.resolveBrandName(personFallback);
    if (!brandName) {
      return;
    }
    const slug = suggestWebsiteSlugFromName(brandName);
    if (!slug) {
      patchWebsiteClaim({ name: brandName });
      return;
    }
    patchWebsiteClaim({ name: brandName, slug });
    this.claimBusinessName = brandName;
    this.claimSlug = slug;
  }

  private navigateAfterAuth(user: SessionUser): Promise<boolean> {
    return navigateAfterStaffAuth({
      user,
      router: this.router,
      tenantContext: this.tenantContext,
      locationContext: this.locationContext,
      adminShellMode: this.adminShellMode,
    });
  }
}
